<template>
  <div class="login-container">
    <form @submit.prevent="handleLogin">
      <div class="form-group mb-3">
        <label for="email" class="mb-1">{{ t("login.email") }}</label>
        <input
          id="email"
          v-model="email"
          class="form-control"
          required
          type="email"
          @invalid="(event) => setCustomValidation(event, 'email_required')"
          @input="clearCustomValidation"
        />
      </div>

      <div class="form-group mb-3">
        <label for="password" class="mb-1">{{ t("login.password") }}</label>
        <div class="position-relative">
          <input
            id="password"
            :type="showPassword ? 'text' : 'password'"
            v-model="password"
            class="form-control"
            required
            @input="clearCustomValidation"
            @invalid="
              (event) => setCustomValidation(event, 'password_required')
            "
          />
          <span class="password-toggle" @click="togglePasswordVisibility">
            <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
          </span>
        </div>
      </div>

      <button class="btn btn-primary w-100" type="submit">
        {{ loading ? t("login.loading") : t("login.button") }}
      </button>

      <div class="text-danger text-center mt-3" v-html="errorMessage || '&nbsp;'"></div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import auth from "../services/authService";

const { t } = useI18n();
const router = useRouter();
const store = useStore();

const email = ref("");
const password = ref("");
const loading = ref(false);
const errorMessage = ref("");
const showPassword = ref(false);

const isAuthenticated = computed(() => store.getters["auth/isAuthenticated"]);

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value;
};

const setCustomValidation = (event, messageKey) => {
  event.target.setCustomValidity(t(`validation.${messageKey}`));
};

const clearCustomValidation = (event) => {
  event.target.setCustomValidity('');
};

const handleLogin = async () => {
  loading.value = true;
  errorMessage.value = "";

  try {
    const response = await auth.login({
      email: email.value,
      password: password.value,
    });

    if (response.success) {
      await new Promise((resolve) => setTimeout(resolve, 300));
      if (isAuthenticated.value) {
        router.push("/dashboard");
      }
    } else {
      errorMessage.value = response.translatedMessage;
    }
  } catch (error) {
    console.error('Login error:', error);
    errorMessage.value =
      error?.translatedMessage ||
      error?.message ||
      t("auth.login_failed");
  } finally {
    loading.value = false;
  }
};

</script>

<style scoped>
.login-container {
  width: 100%;
  max-width: 400px;
  margin: 20px auto;
  padding: 20px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.position-relative {
  position: relative;
}

.password-input {
  padding-right: 40px;
}

.password-toggle {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  font-size: 1.2rem;
  color: #6c757d;
}

.password-toggle:hover {
  color: #000;
}
</style>

